import React, { useState, useRef, useEffect } from 'react'
import clsx from 'clsx'

// components
import Image from '/react4xp/components/Image'

// prop types
interface VideoProps {
   className?: string
   video: {
      url: string
      videoType: 'youtube' | 'vimeo' | 'default'
      poster?: string
      posterMedium?: string
   }
}

const playIcon = (
   <svg width="76" height="86" viewBox="0 0 76 86" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M72.8752 38.8865C76.0418 40.7147 76.0418 45.2853 72.8752 47.1135L7.62475 84.7859C4.45819 86.6141 0.499996 84.3288 0.499996 80.6724L0.499999 5.32761C0.499999 1.67119 4.45819 -0.614085 7.62475 1.21413L72.8752 38.8865Z" fill="white" fillOpacity="0.8" />
   </svg>
)

export default function Video({ video, className }: VideoProps) {

   const [isPlaying, setIsPlaying] = useState(false)
   const [currentPoster, setCurrentPoster] = useState(video.poster)
   const [showPoster, setShowPoster] = useState(true)
   const videoRef = useRef<HTMLVideoElement>(null)
   const iframeRef = useRef<HTMLIFrameElement>(null)
   const [mute, setMute] = useState(0)

   const handleVideoClick = () => {

      if (video.videoType === 'default') {
         if (isPlaying) {
            videoRef.current?.pause()
         } else {
            videoRef.current?.play()
         }
      } else if (video.videoType === 'youtube') {
         iframeRef.current?.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*')
      } else if (video.videoType === 'vimeo') {
         iframeRef.current?.contentWindow?.postMessage('{"method":"play"}', '*')
      }

      setIsPlaying(!isPlaying)
      setShowPoster(false)
   }

   useEffect(() => {
      const handlePlayPause = () => {
         setIsPlaying(!videoRef.current?.paused)
      }

      const videoElement = videoRef.current
      videoElement?.addEventListener('play', handlePlayPause)
      videoElement?.addEventListener('pause', handlePlayPause)

      return () => {
         videoElement?.removeEventListener('play', handlePlayPause)
         videoElement?.removeEventListener('pause', handlePlayPause)
      }
   }, [])

   // set mute based on the screen size
   const setMuteValue = () => {
      setMute(window.innerWidth > 1023 ? 0 : 1)
   }

   useEffect(() => {
      setMuteValue()
      window.addEventListener('resize', setMuteValue)
      return () => {
         window.removeEventListener('resize', setMuteValue)
      }
   }, [])

   return (
      <div
         className={clsx('video-wrapper', className)}
         onClick={handleVideoClick}
      >

         {!isPlaying && video?.poster && (
            <div className='video-wrapper__play-pause'>
               {playIcon}
            </div>
         )}

         {!isPlaying && video?.videoType === 'default' && (
            <div className='video-wrapper__play-pause'>
               {playIcon}
            </div>
         )}

         {video.videoType === 'default' && (
            <>
               <video
                  ref={videoRef}
                  className='cover'
                  loop
                  poster={currentPoster}
                  controls={isPlaying}
               >
                  <source src={video.url + '#t=0.001'} />
               </video>

               {!isPlaying && showPoster && currentPoster && (
                  <Image
                     alt='Video'
                     src={currentPoster}
                     className='cover thumb'
                  />
               )}
            </>
         )}

         {video.videoType === 'youtube' && (
            <>
               <iframe
                  ref={iframeRef}
                  className='cover'
                  src={`https://www.youtube.com/embed/${video.url}?autoplay=0&playsinline=1&mute=${mute}&loop=0&modestbranding=1&rel=0&iv_load_policy=3&title=0&controls=1&portrait=0&enablejsapi=1`}
                  allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                  allowFullScreen
               ></iframe>

               {!isPlaying && showPoster && currentPoster && (
                  <Image
                     alt='Video'
                     src={currentPoster}
                     className='cover thumb'
                  />
               )}
            </>
         )}

         {video.videoType === 'vimeo' && (
            <>
               <iframe
                  ref={iframeRef}
                  className='cover'
                  src={`https://player.vimeo.com/video/${video.url.split('/').pop()}?title=0&byline=0&portrait=0&controls=1`}
                  allow='autoplay; fullscreen; picture-in-picture;'
                  allowFullScreen
               ></iframe>

               {!isPlaying && currentPoster && (
                  <Image
                     alt='Video'
                     src={currentPoster}
                     className='cover thumb'
                  />
               )}
            </>
         )}

      </div>
   )
}